import { datadogLogs } from '@datadog/browser-logs';
import { logContext } from '@shared/services';
import { useQueryClient } from '@tanstack/react-query';
import { TRPCClientError } from '@trpc/client';
import { appStore } from '@web-app/atoms/appStore';
import {
  type AppointmentSubject,
  closeDrawerAtom,
  currentRouteAtom,
  drawerOpenAtom,
  drawerSideAtom,
  type LockedAvailabilitySubject,
  openRouteAtom,
  replaceRouteAtom,
} from '@web-app/atoms/scheduling/schedulingDrawerAtoms';
import {
  appointmentTypesQueryAtom,
  availableDatesAtom,
  availableSlotsQueryAtom,
  bookableDaySlotsQueryAtom,
  calendarMonthAtom,
  effectiveAppointmentTypeIdAtom,
  isBookableSlotsLoadingAtom,
  resetSchedulingStateAtom,
  SCHEDULING_QUERY_KEYS,
  selectedDateAtom,
  selectedDateTimeSlotsAtom,
  selectedSlotViewAtom,
  selectedTimeSlotAtom,
} from '@web-app/atoms/scheduling/schedulingStateAtoms';
import { EVENT_NAMES, useAnalytics } from '@web-app/context/AnalyticsContext';
import { useProviderTimezone } from '@web-app/hooks/availability/useProviderTimezone';
import { useCalendarAction } from '@web-app/hooks/calendar/useCalendarAction';
import {
  PATIENT_DETAILS_QUERY_KEY,
  UPCOMING_APPOINTMENTS_QUERY_KEY,
} from '@web-app/hooks/consts';
import { useGetPatientDetails } from '@web-app/hooks/queries/patient/useGetPatientDetails';
import { usePatientScheduleConflicts } from '@web-app/hooks/scheduling/usePatientScheduleConflicts';
import type {
  CalendarAppointment,
  UpcomingAppointment,
} from '@web-app/server/services/appointments/types';
import type {
  OneTimeKey,
  WindowKey,
} from '@web-app/server/services/availability';
import {
  getTimezoneName,
  getTimezoneOffsetLabel,
  isSameTimezone,
  todayInTimezone,
} from '@web-app/utils/date-time.utils';
import { useAddToCalendarDayWeekMonth } from '@web-app/utils/feature-flags/add-to-calendar-day-week-month';
import {
  dateYmdInMonth,
  firstSelectableDayInMonth,
} from '@web-app/utils/scheduling/selectable-day';
import {
  isAllowedTimeSlot,
  slotsRepresentSameInstant,
  toHealthieBookingDatetime,
  toLocalSlotString,
} from '@web-app/utils/scheduling/slot-datetime';
import { trpc } from '@web-app/utils/trpc';
import { addDays, format, parseISO } from 'date-fns';
import { atom, useAtom, useAtomValue, useSetAtom } from 'jotai';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { toast } from 'sonner';

/**
 * Explicit store option — ensures all scheduling atoms read/write from the
 * app-level store, not from Daily's nested Jotai Provider.
 *
 * See atoms/appStore.ts for why this is necessary.
 */
const STORE = { store: appStore };
const PATIENT_SCHEDULE_TOAST = {
  conflict: {
    title: 'Patient already has an appointment',
    description:
      'This patient already has an appointment at this time. Please select another time.',
  },
  checkFailed: {
    title: 'Could not verify patient schedule',
    description: "We couldn't verify the patient's schedule. Please try again.",
  },
} as const;

// ============================================================================
// Public API — used by callers (AppointmentStatusCard, PatientProfileContent)
// ============================================================================

export interface OpenScheduleOptions {
  side?: 'left' | 'right';
}

/**
 * Opens / closes the global scheduling drawer from any page.
 *
 * Usage:
 *   const { openSchedule, openReschedule, close } = useSchedulingDrawer();
 *   openSchedule(patientId, patientName, { side: 'left' });
 */
export function useSchedulingDrawer() {
  const openRoute = useSetAtom(openRouteAtom, STORE);
  const replaceRoute = useSetAtom(replaceRouteAtom, STORE);
  const closeDrawer = useSetAtom(closeDrawerAtom, STORE);
  const setSide = useSetAtom(drawerSideAtom, STORE);
  const resetState = useSetAtom(resetSchedulingStateAtom, STORE);

  const { trackEvent } = useAnalytics();

  const openSchedule = useCallback(
    (patientId: string, patientName: string, options?: OpenScheduleOptions) => {
      setSide(options?.side ?? 'right');
      openRoute({
        kind: 'appointment',
        mode: 'create',
        subject: {
          patientId,
          patientName,
          bookingMode: 'book',
          existingAppointment: null,
        },
      });

      datadogLogs.logger.info(
        'Scheduling drawer opened',
        logContext('scheduling', 'drawer-open', {
          patientId,
          mode: 'book',
          detail:
            'Provider opened scheduling drawer to book a follow-up appointment',
        }),
      );
      trackEvent(EVENT_NAMES.SCHEDULING_DRAWER_OPENED, {
        patientId,
        mode: 'book',
      });
    },
    [openRoute, setSide, trackEvent],
  );

  const openReschedule = useCallback(
    (
      patientId: string,
      patientName: string,
      appointment: UpcomingAppointment,
      options?: OpenScheduleOptions,
    ) => {
      setSide(options?.side ?? 'right');
      openRoute({
        kind: 'appointment',
        mode: 'create',
        subject: {
          patientId,
          patientName,
          bookingMode: 'reschedule',
          existingAppointment: appointment,
        },
      });

      datadogLogs.logger.info(
        'Scheduling drawer opened',
        logContext('scheduling', 'drawer-open', {
          patientId,
          mode: 'reschedule',
          existingAppointmentId: appointment.id,
          detail:
            'Provider opened scheduling drawer to reschedule an existing appointment',
        }),
      );
      trackEvent(EVENT_NAMES.SCHEDULING_DRAWER_OPENED, {
        patientId,
        mode: 'reschedule',
        existingAppointment: {
          id: appointment.id,
          date: appointment.date,
          start: appointment.start,
        },
      });
    },
    [openRoute, setSide, trackEvent],
  );

  const openDetail = useCallback(
    (appointment: CalendarAppointment, options?: OpenScheduleOptions) => {
      setSide(options?.side ?? 'right');
      openRoute({ kind: 'appointment', mode: 'detail', subject: appointment });

      datadogLogs.logger.info(
        'Scheduling drawer opened',
        logContext('scheduling', 'drawer-open', {
          appointmentId: appointment.id,
          mode: 'detail',
          detail:
            'Provider opened the appointment detail page from the calendar',
        }),
      );
    },
    [openRoute, setSide],
  );

  /**
   * Opens the drawer to book a new appointment with no patient pre-selected
   * (calendar "Add to calendar"). Renders the single booking page with an inline
   * patient picker at the top; selecting a patient fills it in place.
   */
  const openScheduleBlank = useCallback(
    (options?: OpenScheduleOptions) => {
      setSide(options?.side ?? 'right');
      // No patient yet → the booking page shows the inline patient picker.
      openRoute({
        kind: 'appointment',
        mode: 'create',
        subject: {
          patientId: '',
          patientName: '',
          bookingMode: 'book',
          existingAppointment: null,
          allowPatientSelect: true,
        },
      });

      datadogLogs.logger.info(
        'Scheduling drawer opened',
        logContext('scheduling', 'drawer-open', {
          mode: 'book',
          detail:
            'Provider opened scheduling drawer from the calendar without a pre-selected patient',
        }),
      );
      trackEvent(EVENT_NAMES.SCHEDULING_DRAWER_OPENED, { mode: 'book' });
    },
    [openRoute, setSide, trackEvent],
  );

  /** Opens the drawer to the availability editor page. */
  const openAvailability = useCallback(
    (options?: OpenScheduleOptions) => {
      setSide(options?.side ?? 'right');
      openRoute({ kind: 'availability', mode: 'create' });

      datadogLogs.logger.info(
        'Scheduling drawer opened',
        logContext('scheduling', 'drawer-open', {
          mode: 'availability',
          detail: 'Provider opened the availability editor',
        }),
      );
      trackEvent(EVENT_NAMES.SCHEDULING_DRAWER_OPENED, {
        mode: 'availability',
      });
    },
    [openRoute, setSide, trackEvent],
  );

  /**
   * Opens the availability editor for an existing window.
   * Pass occurrenceDate (YYYY-MM-DD) from a calendar click for scoped edit/delete.
   */
  /** Opens the editor for an existing one-day window. No scope to pass. */
  const openOneTimeAvailabilityEdit = useCallback(
    (oneTimeWindow: OneTimeKey, options?: OpenScheduleOptions) => {
      setSide(options?.side ?? 'right');
      openRoute({
        kind: 'availability',
        mode: 'edit',
        subject: { oneTimeWindow },
      });
      trackEvent(EVENT_NAMES.SCHEDULING_DRAWER_OPENED, {
        mode: 'availability',
      });
    },
    [openRoute, setSide, trackEvent],
  );

  const openLockedAvailability = useCallback(
    (subject: LockedAvailabilitySubject, options?: OpenScheduleOptions) => {
      setSide(options?.side ?? 'right');
      openRoute({ kind: 'availability', mode: 'edit', subject });
      trackEvent(EVENT_NAMES.SCHEDULING_DRAWER_OPENED, {
        mode: 'availability',
      });
    },
    [openRoute, setSide, trackEvent],
  );

  const openAvailabilityEdit = useCallback(
    (
      window: WindowKey,
      options?: OpenScheduleOptions & { occurrenceDate?: string },
    ) => {
      setSide(options?.side ?? 'right');
      openRoute({
        kind: 'availability',
        mode: 'edit',
        subject: {
          window,
          ...(options?.occurrenceDate
            ? { occurrenceDate: options.occurrenceDate }
            : {}),
        },
      });

      datadogLogs.logger.info(
        'Scheduling drawer opened',
        logContext('scheduling', 'drawer-open', {
          mode: 'availability',
          detail: 'Provider opened the availability editor to edit a window',
        }),
      );
      trackEvent(EVENT_NAMES.SCHEDULING_DRAWER_OPENED, {
        mode: 'availability',
      });
    },
    [openRoute, setSide, trackEvent],
  );

  /** Opens the drawer to the block-time (out-of-office) page. */
  const openBlock = useCallback(
    (options?: OpenScheduleOptions) => {
      setSide(options?.side ?? 'right');
      openRoute({ kind: 'block', mode: 'create' });

      datadogLogs.logger.info(
        'Scheduling drawer opened',
        logContext('scheduling', 'drawer-open', {
          mode: 'block',
          detail: 'Provider opened the drawer to block out time',
        }),
      );
      trackEvent(EVENT_NAMES.SCHEDULING_DRAWER_OPENED, { mode: 'block' });
    },
    [openRoute, setSide, trackEvent],
  );

  /** Opens the drawer to edit an existing block (prefilled from the block). */
  const openBlockEdit = useCallback(
    (block: CalendarAppointment, options?: OpenScheduleOptions) => {
      setSide(options?.side ?? 'right');
      openRoute({ kind: 'block', mode: 'edit', subject: block });

      datadogLogs.logger.info(
        'Scheduling drawer opened',
        logContext('scheduling', 'drawer-open', {
          appointmentId: block.id,
          mode: 'block-edit',
          detail: 'Provider opened the drawer to edit a blocked time',
        }),
      );
    },
    [openRoute, setSide],
  );

  /**
   * Switches the current drawer page between the appointment-create flow and the
   * block-time flow in place (no new history entry) — backs the Appointment |
   * Time block tab toggle at the top of the booking drawer.
   */
  const switchToBlock = useCallback(() => {
    replaceRoute({ kind: 'block', mode: 'create' });
  }, [replaceRoute]);

  const switchToAppointment = useCallback(() => {
    replaceRoute({
      kind: 'appointment',
      mode: 'create',
      subject: {
        patientId: '',
        patientName: '',
        bookingMode: 'book',
        existingAppointment: null,
        allowPatientSelect: true,
      },
    });
  }, [replaceRoute]);

  /**
   * Commits the chosen patient into the current booking page in place (no new
   * history entry) so the slot-picker below loads for that patient — backs the
   * inline patient picker on the single "Add to calendar" page.
   */
  const selectPatient = useCallback(
    (patientId: string, patientName: string) => {
      replaceRoute({
        kind: 'appointment',
        mode: 'create',
        subject: {
          patientId,
          patientName,
          bookingMode: 'book',
          existingAppointment: null,
          allowPatientSelect: true,
        },
      });
    },
    [replaceRoute],
  );

  /** Clears the chosen patient, returning the booking page to the picker. */
  const clearPatient = useCallback(() => {
    replaceRoute({
      kind: 'appointment',
      mode: 'create',
      subject: {
        patientId: '',
        patientName: '',
        bookingMode: 'book',
        existingAppointment: null,
        allowPatientSelect: true,
      },
    });
  }, [replaceRoute]);

  const close = useCallback(() => {
    closeDrawer();
    resetState();
  }, [closeDrawer, resetState]);

  return {
    openSchedule,
    openScheduleBlank,
    openReschedule,
    openDetail,
    openBlock,
    openBlockEdit,
    openAvailability,
    openAvailabilityEdit,
    openOneTimeAvailabilityEdit,
    openLockedAvailability,
    switchToBlock,
    switchToAppointment,
    selectPatient,
    clearPatient,
    close,
  };
}

// ============================================================================
// Internal API — used only by SchedulingDrawer component
// ============================================================================

/**
 * Orchestrates the scheduling drawer's internal state: patient context,
 * auto-date selection, confirm/cancel, mutations, and toasts.
 *
 * Only consumed by SchedulingDrawer.tsx.
 */
// R-1: Focused derived atoms — subscribe to .data/.isError only, not the full
// query atom (avoids re-renders on every query status transition).
const typesIsErrorAtom = atom((get) => get(appointmentTypesQueryAtom).isError);
const typesIsPendingAtom = atom(
  (get) => get(appointmentTypesQueryAtom).isPending,
);
const availableSlotsIsErrorAtom = atom(
  (get) => get(availableSlotsQueryAtom).isError,
);
const bookableSlotsIsErrorAtom = atom(
  (get) => get(bookableDaySlotsQueryAtom).isError,
);
const slotsRefetchAtom = atom((get) => {
  const refetchAvailable = get(availableSlotsQueryAtom).refetch;
  const refetchBookable = get(bookableDaySlotsQueryAtom).refetch;
  return () => Promise.all([refetchAvailable(), refetchBookable()]);
});

export function useSchedulingDrawerInternal() {
  const isOpen = useAtomValue(drawerOpenAtom, STORE);
  const route = useAtomValue(currentRouteAtom, STORE);
  // This hook drives only the booking Form (appointment/create); the subject is
  // always an AppointmentSubject there.
  const subject = route?.subject as AppointmentSubject | undefined;
  const patientId = subject?.patientId ?? null;
  const patientNameFromRoute = subject?.patientName ?? null;
  const existingAppointment = subject?.existingAppointment ?? null;
  const allowPatientSelect = subject?.allowPatientSelect ?? false;
  const side = useAtomValue(drawerSideAtom, STORE);

  const [selectedDate, setSelectedDate] = useAtom(selectedDateAtom, STORE);
  const [selectedTimeSlot, setSelectedTimeSlot] = useAtom(
    selectedTimeSlotAtom,
    STORE,
  );
  const effectiveAppointmentTypeId = useAtomValue(
    effectiveAppointmentTypeIdAtom,
    STORE,
  );
  const availableDates = useAtomValue(availableDatesAtom, STORE);
  const startsToday = useAddToCalendarDayWeekMonth();
  const selectedSlotView = useAtomValue(selectedSlotViewAtom, STORE);
  const calendarMonth = useAtomValue(calendarMonthAtom, STORE);
  const setCalendarMonth = useSetAtom(calendarMonthAtom, STORE);
  const timeSlots = useAtomValue(selectedDateTimeSlotsAtom, STORE);
  const isBookableSlotsLoading = useAtomValue(
    isBookableSlotsLoadingAtom,
    STORE,
  );

  // R-1: Use focused derived atoms instead of subscribing to full query atoms
  const typesIsError = useAtomValue(typesIsErrorAtom, STORE);
  const typesIsPending = useAtomValue(typesIsPendingAtom, STORE);
  const availableSlotsIsError = useAtomValue(availableSlotsIsErrorAtom, STORE);
  const bookableSlotsIsError = useAtomValue(bookableSlotsIsErrorAtom, STORE);
  const slotsRefetch = useAtomValue(slotsRefetchAtom, STORE);

  // Resolve patient timezone from Healthie data
  const { data: patientDetails } = useGetPatientDetails(patientId ?? '');
  const patientTz = patientDetails?.timezone ?? null;
  const patientName =
    patientNameFromRoute ??
    (patientDetails
      ? `${patientDetails.firstName ?? ''} ${patientDetails.lastName ?? ''}`.trim()
      : '');

  const { timezone: providerTz, status: providerTimezoneStatus } =
    useProviderTimezone();
  const patientSchedule = usePatientScheduleConflicts({
    patientId,
    date: selectedDate,
    appointmentTypeId: effectiveAppointmentTypeId,
    timezone: providerTz,
    excludeAppointmentId: existingAppointment?.id,
    enabled: startsToday && isOpen,
  });
  const patientConflict =
    !!selectedTimeSlot && patientSchedule.conflicts.includes(selectedTimeSlot);
  const providerToday = providerTz ? todayInTimezone(providerTz) : null;
  const patientTimeNotice = useMemo(() => {
    if (!patientTz || !providerTz || isSameTimezone(patientTz, providerTz)) {
      return null;
    }
    const firstName = patientName.split(' ')[0];
    const tzName = getTimezoneName(patientTz);
    const offsetLabel = getTimezoneOffsetLabel(providerTz, patientTz);
    const highlight = offsetLabel ? `${tzName} (${offsetLabel})` : tzName;
    return { prefix: `${firstName} is in the `, highlight };
  }, [patientTz, providerTz, patientName]);

  const { trackEvent } = useAnalytics();
  const trackCalendarAction = useCalendarAction();
  const utils = trpc.useUtils();
  const queryClient = useQueryClient();

  const createMutation = trpc.scheduling.createAppointment.useMutation();
  const rescheduleMutation =
    trpc.scheduling.rescheduleAppointment.useMutation();
  const restoreStatusMutation =
    trpc.scheduling.restoreAppointmentStatus.useMutation();

  const hasError =
    typesIsError ||
    availableSlotsIsError ||
    bookableSlotsIsError ||
    providerTimezoneStatus === 'error';
  const isInitialLoading =
    !hasError && (typesIsPending || providerTimezoneStatus === 'loading');

  // Tracks whether the initial open selection has fired. Prevents the
  // month-change effect from overriding the "prefer ~1 week out" heuristic
  // that autoSelectDateOnOpen applies on first open.
  const initialSelectDoneRef = useRef(false);

  // Reset the ref when the drawer closes so the next open gets the heuristic.
  useEffect(
    function resetInitialSelectOnClose() {
      if (!isOpen) initialSelectDoneRef.current = false;
    },
    [isOpen],
  );

  // --- Auto-select date when drawer opens ---
  useEffect(
    function autoSelectDateOnOpen() {
      if (!isOpen || selectedDate || !providerTz || !providerToday) return;

      // Reschedule opens on the appointment's own day, which needs the profile
      // TZ to map its start into a local slot. A day that has already passed
      // would leave nothing to pick (every slot elapsed) and strand the picker
      // on a month before the calendar's first, so it starts like a new booking.
      const existingSlot = existingAppointment
        ? toLocalSlotString(existingAppointment.start, providerTz)
        : null;
      if (existingSlot && existingSlot.substring(0, 10) >= providerToday) {
        const existingDate = existingSlot.substring(0, 10);
        setSelectedDate(existingDate);
        setSelectedTimeSlot(existingSlot);
        setCalendarMonth(parseISO(existingDate));
        initialSelectDoneRef.current = true;
      } else {
        const oneWeekOut = format(
          addDays(parseISO(providerToday), 7),
          'yyyy-MM-dd',
        );
        // The Day / Week / Month picker opens on the current week, so it starts
        // on today; the original picker starts on a day about a week out.
        const targetDate = startsToday
          ? providerToday
          : availableDates.length > 0
            ? (availableDates.find((d) => d >= oneWeekOut) ?? availableDates[0])
            : oneWeekOut;
        setSelectedDate(targetDate);
        setCalendarMonth(parseISO(targetDate));
        initialSelectDoneRef.current = true;
      }
    },
    [
      isOpen,
      selectedDate,
      existingAppointment,
      availableDates,
      startsToday,
      providerTz,
      providerToday,
      setSelectedDate,
      setSelectedTimeSlot,
      setCalendarMonth,
    ],
  );

  // --- Auto-select first available date after month/type change ---
  useEffect(
    function autoSelectDateOnMonthChange() {
      if (
        !isOpen ||
        !initialSelectDoneRef.current ||
        !selectedDate ||
        !calendarMonth ||
        !providerToday
      )
        return;
      if (dateYmdInMonth(selectedDate, calendarMonth)) return;
      const targetDate = firstSelectableDayInMonth(
        calendarMonth,
        parseISO(providerToday),
      );
      setSelectedDate(targetDate);
      setSelectedTimeSlot(null);
    },
    [
      isOpen,
      calendarMonth,
      selectedDate,
      providerToday,
      setSelectedDate,
      setSelectedTimeSlot,
    ],
  );

  const isExistingSeed =
    !!existingAppointment &&
    !!selectedTimeSlot &&
    !!providerTz &&
    slotsRepresentSameInstant(
      selectedTimeSlot,
      existingAppointment.start,
      providerTz,
    );

  // With the Day / Week / Month picker every time the day lists can be chosen,
  // one that overlaps an entry on the calendar too: booking over it is the
  // provider's call, as in Healthie (the summary warns and a dialog confirms).
  // The original picker offers only the free times. A visit being moved keeps its
  // own start chosen, off the quarter-hour grid or not.
  const slotsLoading = startsToday
    ? selectedSlotView.isLoading
    : isBookableSlotsLoading;
  const isChoosable =
    !!selectedTimeSlot &&
    !!providerTz &&
    (startsToday
      ? selectedSlotView.status !== undefined || isExistingSeed
      : isAllowedTimeSlot(
          selectedTimeSlot,
          timeSlots,
          existingAppointment?.start,
          providerTz,
        ));
  const overlapsExisting = startsToday && selectedSlotView.status === 'booked';

  // Drop time if new appointment length removes the selected start.
  useEffect(
    function clearTimeSlotIfInvalidForType() {
      if (selectedTimeSlot && providerTz && !slotsLoading && !isChoosable) {
        setSelectedTimeSlot(null);
      }
    },
    [
      selectedTimeSlot,
      providerTz,
      slotsLoading,
      isChoosable,
      setSelectedTimeSlot,
    ],
  );

  // patientId required — slot picker can load before patient is chosen.
  const allFieldsSelected =
    !!patientId &&
    !!selectedDate &&
    !!selectedTimeSlot &&
    !!effectiveAppointmentTypeId;

  const isUnchangedFromExisting =
    isExistingSeed &&
    !!selectedTimeSlot &&
    selectedDate === selectedTimeSlot.substring(0, 10);

  const canConfirm =
    allFieldsSelected &&
    !isUnchangedFromExisting &&
    !slotsLoading &&
    !!providerTz &&
    isChoosable &&
    !patientConflict;

  // Inline close logic — avoids circular call to useSchedulingDrawer() which
  // would double all atom subscriptions.
  const closeDrawer = useSetAtom(closeDrawerAtom, STORE);
  const resetDrawerState = useSetAtom(resetSchedulingStateAtom, STORE);

  const handleClose = useCallback(() => {
    closeDrawer();
    resetDrawerState();
  }, [closeDrawer, resetDrawerState]);

  // Invalidating (not refetching the atom) leaves the availability-aware list
  // alone where it is not mounted, so the original picker issues no extra read.
  const refreshSlots = () => {
    slotsRefetch();
    queryClient.invalidateQueries({
      queryKey: [SCHEDULING_QUERY_KEYS.daySlotsWithStatus],
    });
    // The picker's week grid and month counts read the same calendar, so a new
    // booking (or the clash that refused one) must show up there.
    utils.scheduling.getCalendarAppointments.invalidate();
    utils.scheduling.getMonthSlotSummary.invalidate();
    utils.scheduling.patientScheduleConflictsForDay.invalidate();
  };

  // Guard against double-submit during the reschedule two-step flow
  const isSubmittingRef = useRef(false);

  const handleConfirm = async () => {
    if (
      !canConfirm ||
      isSubmittingRef.current ||
      !effectiveAppointmentTypeId ||
      !selectedTimeSlot
    )
      return;
    isSubmittingRef.current = true;

    try {
      await handleConfirmInner(effectiveAppointmentTypeId, selectedTimeSlot);
    } finally {
      isSubmittingRef.current = false;
    }
  };

  const handleConfirmInner = async (
    appointmentTypeId: string,
    timeSlot: string,
  ) => {
    if (!providerTz) return;
    const timezone = providerTz;
    const isReschedule = !!existingAppointment;

    if (isReschedule) {
      try {
        const bookingDatetime = toHealthieBookingDatetime(timeSlot, timezone);
        const check = await utils.scheduling.checkPatientScheduleConflict.fetch(
          {
            patientId: patientId ?? '',
            datetime: bookingDatetime,
            appointmentTypeId,
            timezone,
            excludeAppointmentId: existingAppointment.id,
          },
          { staleTime: 0 },
        );
        if (check.conflict) {
          const { title, description } = PATIENT_SCHEDULE_TOAST.conflict;
          toast.error(title, { description });
          refreshSlots();
          return;
        }
      } catch (error) {
        const forbidden =
          error instanceof TRPCClientError && error.data?.code === 'FORBIDDEN';
        if (!forbidden) {
          const { title, description } = PATIENT_SCHEDULE_TOAST.checkFailed;
          toast.error(title, { description });
          return;
        }
      }
      try {
        await rescheduleMutation.mutateAsync({
          appointmentId: existingAppointment.id,
        });
      } catch (error) {
        datadogLogs.logger.error(
          'Reschedule failed: could not update appointment status',
          logContext('scheduling', 'reschedule-error', {
            patientId,
            existingAppointmentId: existingAppointment.id,
            detail:
              'Step 1 of reschedule failed — old appointment not marked Re-Scheduled, no new appointment created',
          }),
          error instanceof Error ? error : undefined,
        );
        toast.error('Failed to reschedule', {
          description:
            'Failed to reschedule existing appointment. Please try again.',
        });
        return;
      }
    }

    try {
      const bookingDatetime = toHealthieBookingDatetime(timeSlot, timezone);
      const { appointmentId } = await createMutation.mutateAsync({
        patientId: patientId ?? '',
        appointmentTypeId,
        datetime: bookingDatetime,
        timezone,
      });

      datadogLogs.logger.info(
        isReschedule ? 'Appointment rescheduled' : 'Appointment booked',
        logContext('scheduling', isReschedule ? 'reschedule' : 'booking', {
          patientId,
          appointmentTypeId,
          ...(isReschedule
            ? { previousAppointmentId: existingAppointment.id }
            : {}),
          // Booked over an entry on the calendar, on the provider's say-so.
          ...(overlapsExisting ? { overlapsExistingEntry: true } : {}),
          detail: isReschedule
            ? 'Old appointment marked Re-Scheduled and new appointment created'
            : 'Follow-up appointment booked from scheduling drawer',
        }),
      );

      trackEvent(EVENT_NAMES.SCHEDULING_APPOINTMENT_BOOKED, {
        patientId,
        appointmentTypeId,
        datetime: bookingDatetime,
        mode: isReschedule ? 'reschedule' : 'book',
      });
      trackCalendarAction({
        action: 'create',
        entityType: 'appointment',
        appointmentId,
        patientId: patientId ?? undefined,
        appointmentTypeId,
        datetime: bookingDatetime,
        timezone,
        mode: isReschedule ? 'reschedule' : 'book',
        ...(isReschedule
          ? { previousAppointmentId: existingAppointment.id }
          : {}),
      });

      // Invalidate queries so callers (AppointmentStatusCard, sidebar NextAppointment) refresh
      utils.scheduling.getUpcomingForPatient.invalidate({
        patientId: patientId ?? '',
      });
      // Refresh the calendar grid when the drawer was opened from the calendar.
      // No-op for in-session callers — that query isn't mounted there.
      utils.scheduling.getCalendarAppointments.invalidate();
      queryClient.invalidateQueries({
        queryKey: [PATIENT_DETAILS_QUERY_KEY, patientId],
      });
      // The Appointments page list is cached indefinitely; mark it stale.
      queryClient.invalidateQueries({
        queryKey: [UPCOMING_APPOINTMENTS_QUERY_KEY],
      });
      refreshSlots();
      handleClose();

      toast.success(
        isReschedule ? 'Appointment rescheduled' : 'Appointment scheduled',
        {
          description: isReschedule
            ? 'The previous appointment has been marked as rescheduled and a new one has been booked.'
            : 'We have scheduled your follow-up appointment.',
        },
      );
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Unknown error';
      const errorCode =
        error instanceof TRPCClientError ? error.data?.code : undefined;
      const patientToast =
        errorCode === 'UNPROCESSABLE_CONTENT'
          ? PATIENT_SCHEDULE_TOAST.conflict
          : errorCode === 'SERVICE_UNAVAILABLE'
            ? PATIENT_SCHEDULE_TOAST.checkFailed
            : null;
      const isSlotTaken =
        !patientToast &&
        (errorCode === 'CONFLICT' ||
          message.toLowerCase().includes('slot') ||
          message.toLowerCase().includes('available'));

      if (isReschedule) {
        // Attempt to restore the old appointment by clearing its pm_status.
        // Healthie's `without_status: true` filter (used by getUpcomingForPatient)
        // excludes appointments with any pm_status set — even "Scheduled".
        // An empty string clears the status so the appointment reappears.
        try {
          await restoreStatusMutation.mutateAsync({
            appointmentId: existingAppointment.id,
            status: '',
          });
          datadogLogs.logger.info(
            'Reschedule rollback succeeded: old appointment status cleared',
            logContext('scheduling', 'reschedule-rollback', {
              patientId,
              appointmentId: existingAppointment.id,
            }),
          );
        } catch (rollbackError) {
          datadogLogs.logger.error(
            'Reschedule rollback failed: could not restore old appointment status',
            logContext('scheduling', 'reschedule-rollback-error', {
              patientId,
              appointmentId: existingAppointment.id,
              detail:
                'Old appointment remains Re-Scheduled — manual intervention may be needed',
            }),
            rollbackError instanceof Error ? rollbackError : undefined,
          );
        }

        datadogLogs.logger[isSlotTaken ? 'warn' : 'error'](
          isSlotTaken
            ? 'Reschedule failed: time slot unavailable'
            : 'Reschedule failed: new appointment not created',
          logContext('scheduling', 'reschedule-error', {
            patientId,
            previousAppointmentId: existingAppointment.id,
            appointmentTypeId,
            detail: isSlotTaken
              ? 'Step 2 of reschedule failed (slot taken) — rollback attempted'
              : 'Step 2 of reschedule failed — rollback attempted',
          }),
          isSlotTaken ? undefined : error instanceof Error ? error : undefined,
        );
        if (patientToast) {
          toast.error(patientToast.title, {
            description: `${patientToast.description} Your existing appointment has been kept.`,
          });
        } else {
          toast.error(
            isSlotTaken ? 'Time slot unavailable' : 'Booking failed',
            {
              description: isSlotTaken
                ? 'That time slot is no longer available. Your existing appointment has been kept. Please select another time.'
                : 'We couldn’t book the new appointment. Your existing appointment has been kept. Please try again.',
            },
          );
        }
        utils.scheduling.getUpcomingForPatient.invalidate({
          patientId: patientId ?? '',
        });
        // The old appointment may stay Re-Scheduled if the rollback failed.
        queryClient.invalidateQueries({
          queryKey: [UPCOMING_APPOINTMENTS_QUERY_KEY],
        });
        refreshSlots();
        return;
      }

      datadogLogs.logger[isSlotTaken ? 'warn' : 'error'](
        isSlotTaken
          ? 'Booking failed: time slot unavailable'
          : 'Booking failed',
        logContext('scheduling', 'booking-error', {
          patientId,
          appointmentTypeId,
          detail: isSlotTaken
            ? 'Time slot no longer available (race condition)'
            : 'Unexpected error when creating follow-up appointment',
        }),
        isSlotTaken ? undefined : error instanceof Error ? error : undefined,
      );

      if (patientToast) {
        toast.error(patientToast.title, {
          description: patientToast.description,
        });
        refreshSlots();
      } else if (isSlotTaken) {
        toast.error('Time slot unavailable', {
          description:
            'This time slot is no longer available. Please select another time.',
        });
        refreshSlots();
      } else {
        toast.error('Something went wrong', {
          description: 'Please try again.',
        });
      }
    }
  };

  return {
    isOpen,
    side,
    patientId,
    patientName,
    allowPatientSelect,
    patientTz,
    patientTimeNotice,
    providerTimezone: providerTz,
    canConfirm,
    patientConflict,
    patientSchedule,
    overlapsExisting,
    isBooking: createMutation.isPending || rescheduleMutation.isPending,
    isInitialLoading,
    hasError,
    handleClose,
    handleConfirm,
  };
}
